import React from 'react';
import { css } from '@emotion/react';
import {
  Responsive,
  ResponsivePaddingProps,
  ResponsiveHidingProps,
  SpacingToken,
  useFullTheme,
} from '~/design';
import { useResponsive } from '~/utils';
import { useResponsivePaddingStyle, useResponsiveHideStyle } from '~/utils/styling';
import { Breakpoint } from '~/design/breakpoint';

const JUSTIFY_MAPPING = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

const ALIGN_MAPPING = {
  stretch: 'stretch',
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  baseline: 'baseline',
};

const FlexContext = React.createContext(false);

type Direction = 'row' | 'column' | 'column-reverse' | 'row-reverse';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
type Align = 'stretch' | 'start' | 'end' | 'center' | 'baseline';
type FlexBasis = number | string | 'auto';

export interface FlexProps extends ResponsivePaddingProps, ResponsiveHidingProps {
  /** The alignment along the cross axis (default: stretch) */
  align?: Align | Responsive<Align>;

  /** The aria-describedby of the element. */
  'aria-describedby'?: string;

  /** The aria-labelledby of the element. */
  'aria-labelledby'?: string;

  /** The underlying DOM node to render (default: div) */
  as?: keyof JSX.IntrinsicElements;

  /** The default size of the flex container before the remaining space is distributed (default: auto) */
  basis?: FlexBasis | Responsive<FlexBasis>;

  /** The content of the flex container */
  children: React.ReactNode;

  /** The direction flex items are placed in the flex container (default: row). NOTE:
   * `column-reverse` and `row-reverse` will be removed in a future version because they can lead
   * to accessibility issues, especially when used in combination with the `order` CSS property.
   * Please see the "Accessibility" section of the Flex component's docstring. */
  direction?: Direction | Responsive<Direction>;

  /** The space between flex items (default: 0) */
  gap?: SpacingToken | Responsive<SpacingToken>;

  /** The ability for the flex container to grow if necessary (default: 0) */
  grow?: number | Responsive<number>;

  /** The height of the flex container. `height=fill` is handy when you want to align something
   * inside the container to the bottom. */
  height?: 'fill' | 'hug';

  /** The ID of the element. */
  id?: string;

  /** The alignment along the main axis (default: start) */
  justify?: Justify | Responsive<Justify>;

  /** The min-width of the flex container. Use this if you're experiencing weird overflow issues. */
  minWidth?: 0;

  /** The role of the element. */
  role?: string;

  /** The ability for the flex container to shrink if necessary (default: 1) */
  shrink?: number | Responsive<number>;

  /** Whether flex items are forced onto one line or can wrap onto multiple lines (default: false)  */
  wrap?: boolean | Responsive<boolean>;
}

/**
 * `Flex` is a layout component that provides a very similar API to Flexbox in CSS.
 * You can also use `Flex.Item` inside `Flex` to have control over individual flex items but it's not required.
 *
 * ## Accessibility ♿
 * - Avoid using `column-reverse` / `row-reverse` values for the `direction` prop. They should
 * rarely be used and may cause the focus/tab order to be unintuitive, potentially violating
 * [WCAG 1.3.2](https://www.w3.org/WAI/WCAG22/quickref/?versions=2.1&showtechniques=132#meaningful-sequence)
 * and [WCAG 2.4.3](https://www.w3.org/WAI/WCAG21/quickref/#focus-order). For more information,
 * refer to [this Confluence document](https://applyboard.atlassian.net/wiki/spaces/UE/pages/2349039867/Common+Accessibility+Mistakes#6.-Using-a-%E2%80%9Creverse%E2%80%9D-value-for-flex-direction-or-using-order).
 * For the same reasons, we do not and will not support the CSS `order` property.
 */
export function Flex({
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy,
  align,
  as: Tag = 'div',
  basis,
  children,
  direction,
  gap,
  grow,
  height,
  hideAbove,
  hideBelow,
  id,
  justify,
  minWidth,
  p,
  pb,
  pl,
  pr,
  pt,
  px,
  py,
  role,
  shrink,
  wrap,
}: FlexProps) {
  const theme = useFullTheme();
  const responsive = useResponsive();

  const paddingStyle = useResponsivePaddingStyle({
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
  });

  const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });

  const style = css({
    display: 'flex',
    minWidth,
    ...responsive.css({
      alignItems: responsive.map(align, prop => prop && ALIGN_MAPPING[prop]),
      flexBasis: basis,
      flexDirection: direction,
      flexGrow: grow,
      flexShrink: shrink,
      flexWrap: responsive.map(wrap, value => (value ? 'wrap' : 'nowrap')),
      gap: responsive.map(gap, prop => prop && theme.spacing[prop]),
      height: height === 'fill' ? '100%' : 'auto',
      justifyContent: responsive.map(justify, prop => prop && JUSTIFY_MAPPING[prop]),
    }),
  });

  return (
    <FlexContext.Provider value={true}>
      <Tag
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        css={[style, paddingStyle, hideStyle]}
        id={id}
        role={role}
      >
        {children}
      </Tag>
    </FlexContext.Provider>
  );
}

type FlexAlign = 'start' | 'center' | 'end' | 'stretch';

export interface FlexItemProps {
  /** The alignment the flex item. This defaults to the `align` prop set on the parent `Flex` component */
  align?: FlexAlign | Responsive<FlexAlign>;

  /** The underlying DOM node to render (default: div) */
  as?: keyof JSX.IntrinsicElements;

  /** The default size of the flex item before the remaining space is distributed (default: auto) */
  basis?: FlexBasis | Responsive<FlexBasis>;

  /** The content of the flex item */
  children: React.ReactNode;

  /** The ability for the flex item to grow if necessary (default: 0) */
  grow?: number | Responsive<number>;

  /** The min-width of the flex item. Use this if you're experiencing weird overflow issues. */
  minWidth?: 0;

  /** The ability for the flex item to shrink if necessary (default : 1) */
  shrink?: number | Responsive<number>;

  /** The width of the container */
  width?: 'fill' | 'auto' | Responsive<'fill' | 'auto'>;

  /** Hides the button above the provided breakpoint size */
  hideAbove?: Breakpoint;

  /** Hides the button below the provided breakpoint size */
  hideBelow?: Breakpoint;
}

/**
 * `Flex.Item` is a child component of `Flex` and allows control on individual flex items.
 */
Flex.Item = function FlexItem({
  align,
  as: Tag = 'div',
  basis,
  children,
  grow,
  minWidth,
  shrink,
  width,
  hideAbove,
  hideBelow,
}: FlexItemProps) {
  if (!React.useContext(FlexContext)) {
    throw new Error('`Flex.Item` can only be rendered inside `Flex`');
  }

  const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });
  const responsive = useResponsive();

  const style = responsive.css({
    alignSelf: responsive.map(align, prop => prop && ALIGN_MAPPING[prop]),
    flexBasis: basis,
    flexGrow: grow,
    flexShrink: shrink,
    minWidth,
    width: responsive.map(width, prop => (prop === 'fill' ? '100%' : 'auto')),
  });

  return <Tag css={[style, hideStyle]}>{children}</Tag>;
};
